import React, { useState, useEffect, useMemo, useContext, Suspense, lazy } from 'react';
import { Link, Route, Routes, useLocation, useNavigate, Navigate } from 'react-router-dom';
import ValidationContentLocalStorage from './ValidationContentLocalStorage';
import { isHeldByPendingVersion } from './CreativeApprovalReuploadModes';
import { MdDashboard, MdSettings } from 'react-icons/md';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { LuUsers } from "react-icons/lu";
import { MdOutlineDashboardCustomize, MdOutlineOndemandVideo, MdRule, MdPendingActions, MdOutlineAnalytics } from 'react-icons/md';
import { FaHistory, FaCube, FaUsers, FaTags, FaRegFolderOpen } from 'react-icons/fa';
import { BsListCheck } from "react-icons/bs";
import { BsDatabaseFillCheck } from "react-icons/bs";
import { MdOutlineFeedback } from "react-icons/md";
import { MdOutlineCategory } from "react-icons/md";
import { FaCaretRight } from "react-icons/fa";
import { FaCaretDown } from "react-icons/fa";
import { FaBuilding } from "react-icons/fa"; // Department feature: Departments sidebar icon
import { FaTable } from "react-icons/fa"; // Rawdump: sidebar icon
import { FaRegImages } from "react-icons/fa"; // Creatives: sidebar icon
import { FaProjectDiagram } from "react-icons/fa"; // Approval Flows: sidebar icon

import './HomeScreen.css';
import { NOTIFICATIONS_CHANGED_EVENT } from './CreativesConstants';
import appConfig from '../config/appConfig.json';
import { getCurrentUser, withEmailId } from '../utils/auth';
// const ChangeDatabase = lazy(() => import('./ChangeDatabase'));
const Dashboard = lazy(() => import('./Dashboard'));
const Users = lazy(() => import('./Users'));
const Departments = lazy(() => import('./Departments')); // Department feature (NEW)
const ApprovalDashboard = lazy(() => import('./ApprovalDashboard')); // Approval Dashboard (NEW, ported from wireframe)
const ApprovalDetailedView = lazy(() => import('./ApprovalDetailedView')); // Approval Detailed View: own page/route, opened from a dashboard row
const Creatives = lazy(() => import('./Creatives')); // Creatives screen (NEW, ported from wireframe)
const SendForApproval = lazy(() => import('./SendForApproval')); // Send for Approval: opened from a creative row
const ApprovalFlows = lazy(() => import('./ApprovalFlows')); // Approval Flows: build and save the flows Send for Approval picks
const Rawdump = lazy(() => import('./Rawdump')); // Rawdump report (NEW, ported from wireframe)
const Registry = lazy(() => import('./Registry')); // Registry: filed (approved + ARN) creatives
const ProgramTypes = lazy(() => import('./ProgramTypes'));
const Rules = lazy(() => import('./Rules'));
const ReferenceDatasets = lazy(() => import('./ReferenceDatasets'));
// const ReferenceDatasets = lazy(() => import('./ReferenceDatasetsV2'));
const ValidateContent = lazy(() => import('./ValidateContent'));
const ValidateVideoContent = lazy(() => import('./ValidateVideoContent'));
const ValidationHistory = lazy(() => import('./ValidationHistory'));
const PendingUserApprovals = lazy(() => import('./PendingUserApprovals'));
const UsageDashboard = lazy(() => import('./UsageDashboard'));
const Subscriptions = lazy(() => import('./Subscriptions'));
const RuleTestPlayground = lazy(() => import('./RuleTestPlayground'));
const Categories = lazy(() => import('./Categories'));
const Feedbacks = lazy(() => import('./Feedbacks'));
const PageNotFound = lazy(() => import('./PageNotFound')); //  Import PageNotFound
const ValidationContainer = lazy(() => import('./ValidationContainer'));
const ValidateContentContainer = lazy(() => import('./ValidateContentContainer'));
const ValidateVideoContentContainer = lazy(() => import('./ValidateVideoContentContainer'));
const ValidationReportAnalysis = lazy(() => import('./ValidationReportAnalysis'));

// Define roles as constants for better maintainability
const roles = {
  OWNER: 'Owner',
  SUPERADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  WRITE: 'Write',
  READ: 'Read',
};

const { applicationType, enableLNCFeature = true, enableDisclaimerView = true } = appConfig;

function NotificationBadge({ count, label }) {
  if (!count) return null;
  return (
    <span
      className="home-screen__badge"
      aria-label={`${count} ${label}`}
      title={`${count} ${label}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}


function HomeScreen({ isSidebarOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  // A re-upload waiting to be kept or thrown away. It lives in the ValidationContent
  // context, which is mounted in App.js and so outlives every route.
  const { pendingDraft } = useContext(ValidationContentLocalStorage);

  useEffect(() => {
    if (!pendingDraft) return;
    if (!isHeldByPendingVersion(pendingDraft.origin, location.pathname)) return;
    navigate('/mcv/validate-content', { replace: true });
  }, [pendingDraft, location.pathname, navigate]);
  const [activeButton, setActiveButton] = useState(location.pathname);
  const [isValidateOpen, setIsValidateOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isRulesSectionOpen, setIsRulesSectionOpen] = useState(false);
  const [isUsersSectionOpen, setIsUsersSectionOpen] = useState(false);
  const [isWorkflowSectionOpen, setIsWorkflowSectionOpen] = useState(false);
  const [isUsageSectionOpen, setIsUsageSectionOpen] = useState(false);
  const [isValidationReportAnalysisSectionOpen, setIsValidationReportAnalysisSectionOpen] = useState(false);
  const [isRegistrySectionOpen, setIsRegistrySectionOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  // With the disclaimer view enabled in appConfig.json, Read users don't get Rules and Categories
  const isDisclaimerViewUser = enableDisclaimerView && userRole === roles.READ;
  const [loading, setLoading] = useState(true);
  const access = useMemo(() => getCurrentUser(), []);
  const [notifications, setNotifications] = useState({ toApprove: 0, sentBack: 0, pendingUsers: 0 });

  useEffect(() => {
    const savedValidateState = localStorage.getItem('isValidateOpen');
    const savedManageState = localStorage.getItem('isManageOpen');
    const savedRulesState = localStorage.getItem('isRulesSectionOpen');
    const savedUsersState = localStorage.getItem('isUsersSectionOpen');
    const savedUsageState = localStorage.getItem('isUsageSectionOpen');
    const userData = JSON.parse(localStorage.getItem('userData'));
    const savedValidationReportAnalysisState = localStorage.getItem('isValidationReportAnalysisSectionOpen');

    if (savedValidateState !== null) {
      setIsValidateOpen(JSON.parse(savedValidateState));
    }
    if (savedManageState !== null) {
      setIsManageOpen(JSON.parse(savedManageState));
    }
    if (savedRulesState !== null) {
      setIsRulesSectionOpen(JSON.parse(savedRulesState));
    }
    if (savedUsersState !== null) {
      setIsUsersSectionOpen(JSON.parse(savedUsersState));
    }
    // Creative-approval workflow: restore the Workflow section collapse state.
    const savedWorkflowState = localStorage.getItem('isWorkflowSectionOpen');
    if (savedWorkflowState !== null) {
      setIsWorkflowSectionOpen(JSON.parse(savedWorkflowState));
    }
    if (savedUsageState !== null) {
      setIsUsageSectionOpen(JSON.parse(savedUsageState));
    }
    if (savedValidationReportAnalysisState !== null) {
      setIsValidationReportAnalysisSectionOpen(JSON.parse(savedValidationReportAnalysisState));
    }
    const savedRegistryState = localStorage.getItem('isRegistrySectionOpen');
    if (savedRegistryState !== null) {
      setIsRegistrySectionOpen(JSON.parse(savedRegistryState));
    }
    if (userData) {
      setUserRole(userData[6]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(withEmailId('/mcv/approval_notifications'));
        if (!response.ok) return;
        const body = await response.json();
        if (cancelled || body.status !== 'SUCCESS') return;
        setNotifications({
          toApprove: body.data?.to_approve || 0,
          sentBack: body.data?.sent_back || 0,
          pendingUsers: body.data?.pending_users || 0,
        });
      } catch (error) {
        // A badge that cannot be drawn is not worth a console full of noise, and
        // the navigation around it still works.
      }
    };

    load();
    const timer = setInterval(load, 60000);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
    };
    // Loaded once, then on the timer. Screens that change a count fire
    // NOTIFICATIONS_CHANGED_EVENT, so page-to-page navigation needs no refetch.
  }, []);

  const handleButtonClick = (path) => {
    setActiveButton(path);
  };

  const toggleValidate = () => {
    const newState = !isValidateOpen;
    setIsValidateOpen(newState);
    localStorage.setItem('isValidateOpen', JSON.stringify(newState));
  };

  const toggleManage = () => {
    const newState = !isManageOpen;
    setIsManageOpen(newState);
    localStorage.setItem('isManageOpen', JSON.stringify(newState));
  };

  const toggleRulesSection = () => {
    const newState = !isRulesSectionOpen;
    setIsRulesSectionOpen(newState);
    localStorage.setItem('isRulesSectionOpen', JSON.stringify(newState));
  };

  const toggleUsersSection = () => {
    const newState = !isUsersSectionOpen;
    setIsUsersSectionOpen(newState);
    localStorage.setItem('isUsersSectionOpen', JSON.stringify(newState));
  };

  // Creative-approval workflow: toggle for the top-level "Workflow" section.
  const toggleWorkflowSection = () => {
    const newState = !isWorkflowSectionOpen;
    setIsWorkflowSectionOpen(newState);
    localStorage.setItem('isWorkflowSectionOpen', JSON.stringify(newState));
  };

  const toggleUsageSection = () => {
    const newState = !isUsageSectionOpen;
    setIsUsageSectionOpen(newState);
    localStorage.setItem('isUsageSectionOpen', JSON.stringify(newState));
  };

  const toggleValidationReportAnalysisSection = () => {
    const newState = !isValidationReportAnalysisSectionOpen;
    setIsValidationReportAnalysisSectionOpen(newState);
    localStorage.setItem('isValidationReportAnalysisSectionOpen', JSON.stringify(newState));
  };

  const toggleRegistrySection = () => {
    const newState = !isRegistrySectionOpen;
    setIsRegistrySectionOpen(newState);
    localStorage.setItem('isRegistrySectionOpen', JSON.stringify(newState));
  };


  const updateActiveButton = (path) => {
    setActiveButton(path); // Pass this function to Dashboard
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="home-screen">
      {isSidebarOpen && (
        <div className={`home-screen__sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
          {(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE || userRole === roles.READ) && (
            <Link
              to="/mcv"
              className={`header__dropdown-button_sidebar ${activeButton === '/mcv' ? 'active' : ''}`}
              onClick={() => handleButtonClick('/mcv')}
            >
              <MdDashboard className="sidebar-icon" />
              Dashboard
            </Link>
          )}

          <hr className='sections-hr-tag' />


          {enableLNCFeature && ([roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.ADMIN || userRole === roles.WRITE || userRole === roles.READ) && (
            <div className="home-screen__section">
              <button className="home-screen__section-button" onClick={toggleWorkflowSection}>
                Workflow <span className="icon-right">{isWorkflowSectionOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
              </button>
              {isWorkflowSectionOpen && (
                <div className="home-screen__section-content">
                  {access.canUseCreatives && (
                    <Link to="/mcv/creatives" className={`home-screen__button ${activeButton === '/mcv/creatives' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/creatives')}>
                      <FaRegImages className='validate-section-icons' /> Creatives
                      <NotificationBadge
                        count={notifications.sentBack}
                        label="creative(s) sent back to you"
                      />
                    </Link>
                  )}
                  {/* Approval Flows: same access as Send for Approval (creators who can send) */}
                  {(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE) && access.canCreate && (
                    <Link to="/mcv/approval-flows" className={`home-screen__button ${activeButton === '/mcv/approval-flows' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/approval-flows')}>
                      <FaProjectDiagram className='validate-section-icons' /> Approval Flows
                    </Link>
                  )}
                  {access.canUseApprovals && (
                    <Link to="/mcv/approval-dashboard" className={`home-screen__button ${activeButton === '/mcv/approval-dashboard' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/approval-dashboard')}>
                      <IoMdCheckmarkCircleOutline className='validate-section-icons' /> Approval Dashboard
                      <NotificationBadge
                        count={notifications.toApprove}
                        label="creative(s) awaiting your decision"
                      />
                    </Link>
                  )}
                  {access.canUseApprovals && (
                    /* Rawdump: department-wise approval-request report (NEW, ported from wireframe). */
                    <Link to="/mcv/rawdump" className={`home-screen__button ${activeButton === '/mcv/rawdump' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/rawdump')}>
                      <FaTable className='validate-section-icons' /> Rawdump
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {enableLNCFeature && <hr className='sections-hr-tag' />}
          {(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE || userRole === roles.READ) && (
            <div className="home-screen__section">
              <button className="home-screen__section-button" onClick={toggleValidate}>
                Validate <span className="icon-right">{isValidateOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
              </button>
              {isValidateOpen && (
                <div className="home-screen__section-content">
                  <Link
                    to="/mcv/validate-content"
                    // Clear any lingering re-upload draft when explicitly clicking sidebar Validate Content
                    state={{ cleanValidate: true }}
                    className={`home-screen__button ${activeButton === '/mcv/validate-content' ? 'active' : ''}`}
                    onClick={() => handleButtonClick('/mcv/validate-content')}
                  >
                    <MdOutlineDashboardCustomize className='validate-section-icons'/> Validate Content
                  </Link>

                  <Link
                    to="/mcv/validate-video-content"
                    className={`home-screen__button ${activeButton === '/mcv/validate-video-content' ? 'active' : ''}`}
                    onClick={() => handleButtonClick('/mcv/validate-video-content')}
                  >
                    <MdOutlineOndemandVideo className='validate-section-icons' /> Validate Video
                  </Link>

                  <Link
                    to="/mcv/validation-history"
                    className={`home-screen__button ${activeButton === '/mcv/validation-history' ? 'active' : ''}`}
                    onClick={() => handleButtonClick('/mcv/validation-history')}
                  >
                    <FaHistory className='validate-section-icons'/> History
                  </Link>
                </div>
              )}
            </div>
          )}

          <hr className='sections-hr-tag' />

          {(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE || userRole === roles.READ) && (
            <div className="home-screen__section">
              <button className="home-screen__section-button" onClick={toggleManage}>
                Manage <span className="icon-right">{isManageOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
              </button>
              {isManageOpen && (
                <div className="home-screen__section-content">

                  {/* Rules Subsection */}
                  <button className="home-screen__subsection-button" onClick={toggleRulesSection}>
                    {/* {isRulesSectionOpen ? <FaCaretRight /> : <FaCaretDown />}  */}
                    Rules <span className="icon-right">{isRulesSectionOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
                  </button>
                  {isRulesSectionOpen && (
                    <div className="home-screen__subsection-content">
                      {/* Rules and Categories are hidden for Read users */}
                      {!isDisclaimerViewUser && (
                        <Link to="/mcv/rules" className={`home-screen__button ${activeButton === '/mcv/rules' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/rules')}>
                          <BsListCheck className='manage-section-icons' /> {enableDisclaimerView ? 'Disclaimer Repository' : 'Rules'}
                        </Link>
                      )}
                      {/* With the disclaimer view, Product Types is called "All Disclaimers" */}
                      <Link to="/mcv/program-types" className={`home-screen__button ${activeButton === '/mcv/program-types' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/program-types')}>
                        <FaCube className='manage-section-icons' /> {enableDisclaimerView ? 'All Disclaimers' : 'Product Types'}
                      </Link>
                      {!isDisclaimerViewUser && (
                        <Link to="/mcv/categories" className={`home-screen__button ${activeButton === '/mcv/categories' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/categories')}>
                          <MdOutlineCategory className='manage-section-icons' /> Categories
                        </Link>
                      )}
                      <Link to="/mcv/reference-datasets" className={`home-screen__button ${activeButton === '/mcv/reference-datasets' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/reference-datasets')}>
                        <BsDatabaseFillCheck className='manage-section-icons' /> Knowledge Base
                      </Link>
                      <Link to="/mcv/feedbacks" className={`home-screen__button ${activeButton === '/mcv/feedbacks' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/feedbacks')}>
                        <MdOutlineFeedback className='manage-section-icons' /> Feedbacks
                      </Link>
                      <Link to="/mcv/rule-test-playground" className={`home-screen__button ${activeButton === '/mcv/rule-test-playground' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/rule-test-playground')}>
                        <BsListCheck className='manage-section-icons' /> Rule Test Playground
                      </Link>
                    </div>
                  )}


                  {/* Validation Report Analysis Subsection */}
                  {(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole)) && (
                    <>
                      <hr className='subsections-hr-tag' />
                      <button className="home-screen__subsection-button" onClick={toggleValidationReportAnalysisSection}>
                        Report Analysis 
                        <span className="icon-right">
                          {isValidationReportAnalysisSectionOpen ? <BsChevronUp /> : <BsChevronDown />}
                        </span>
                      </button>
                    </>
                  )}

                  {isValidationReportAnalysisSectionOpen && (userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole)) && (
                    <div className="home-screen__subsection-content">
                      <Link
                        to="/mcv/report-analysis"
                        className={`home-screen__button ${activeButton === '/mcv/report-analysis' ? 'active' : ''}`}
                        onClick={() => handleButtonClick('/mcv/report-analysis')}
                      >
                        <MdOutlineAnalytics className='manage-section-icons' /> Report Analysis
                      </Link>
                    </div>
                  )}

                  {enableLNCFeature && (
                    <>
                      <hr className='subsections-hr-tag' />
                      <button className="home-screen__subsection-button" onClick={toggleRegistrySection}>
                        Registry
                        <span className="icon-right">
                          {isRegistrySectionOpen ? <BsChevronUp /> : <BsChevronDown />}
                        </span>
                      </button>
                      {isRegistrySectionOpen && (
                        <div className="home-screen__subsection-content">
                          <Link
                            to="/mcv/registry"
                            className={`home-screen__button ${activeButton === '/mcv/registry' ? 'active' : ''}`}
                            onClick={() => handleButtonClick('/mcv/registry')}
                          >
                            <FaRegFolderOpen className='manage-section-icons' /> Registry
                          </Link>
                        </div>
                      )}
                    </>
                  )}

                  {/* Users Subsection */}
                  {([roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.ADMIN || userRole === roles.WRITE) && (
                    <>
                      <hr className='subsections-hr-tag' />
                      <button className="home-screen__subsection-button" onClick={toggleUsersSection}>
                        Users <span className="icon-right">{isUsersSectionOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
                      </button>
                    </>
                  )}
                  {isUsersSectionOpen && (
                    <div className="home-screen__subsection-content">
                      {([roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.ADMIN || userRole === roles.WRITE) && (
                        <Link to="/mcv/users" className={`home-screen__button ${activeButton === '/mcv/users' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/users')}>
                          <LuUsers className='manage-section-icons' /> Users
                        </Link>
                      )}
                      {enableLNCFeature && ([roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.ADMIN || userRole === roles.WRITE) && (
                        <Link to="/mcv/departments" className={`home-screen__button ${activeButton === '/mcv/departments' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/departments')}>
                          <FaBuilding className='manage-section-icons' /> Departments
                        </Link>
                      )}
                      {[roles.SUPERADMIN, roles.OWNER].includes(userRole) && (
                        <Link to="/mcv/pending-approvals" className={`home-screen__button ${activeButton === '/mcv/pending-approvals' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/pending-approvals')}>
                          <MdPendingActions className='manage-section-icons' /> Pending Approvals
                          <NotificationBadge
                            count={notifications.pendingUsers}
                            label="user(s) awaiting approval"
                          />
                        </Link>
                      )}
                    </div>
                  )}

                  {/* Usage Subsection */}
                  {applicationType === 'SaaS' && (
                    <>
                      {[roles.SUPERADMIN, roles.OWNER].includes(userRole) && (
                        <>
                          <hr className='subsections-hr-tag' />
                          <button className="home-screen__subsection-button" onClick={toggleUsageSection}>
                            Usage <span className="icon-right">{isUsageSectionOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
                          </button>
                        </>
                      )}
                      {isUsageSectionOpen &&  (
                        <div className="home-screen__subsection-content">
                          {[roles.SUPERADMIN, roles.OWNER].includes(userRole) && (
                            <>
                              <Link to="/mcv/usage-dashboard" className={`home-screen__button ${activeButton === '/mcv/usage-dashboard' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/usage-dashboard')}>
                                <MdOutlineAnalytics className='manage-section-icons' /> Usage Dashboard
                              </Link>
                              <Link to="/mcv/subscriptions" className={`home-screen__button ${activeButton === '/mcv/subscriptions' ? 'active' : ''}`} onClick={() => handleButtonClick('/mcv/subscriptions')}>
                                <FaTags className='manage-section-icons' /> Subscriptions
                              </Link>
                            </>
                          )}
                        </div>
                      )}
                    </>
                   )} 

                </div>
              )}
            </div>
          )}

          <footer className="sidebar-footer">
            <hr className='sections-hr-tag' />
            <p>
              Product by{' '}
              <a href="https://www.dataeaze.io/" target="_blank" rel="noopener noreferrer" className="footer-link">
                <span className="red-text"> data</span>eaze
              </a>
            </p>
          </footer>
        </div>
      )}

    <div className={`home-screen__content ${isSidebarOpen ? 'with-sidebar' : ''}`}>
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Dashboard updateActiveButton={updateActiveButton} />} />
            {/* <Route path="/validate-content" element={<ValidateContent />} /> */}
            <Route path="/validate-content" element={<ValidateContentContainer />} />
            {/* <Route path="/validate-video-content" element={<ValidateVideoContent />} /> */}
            <Route path="/validate-video-content" element={<ValidateVideoContentContainer />} />
            {/* <Route path="/validation-history" element={<ValidationHistory />} /> */}
            <Route path="/validation-history" element={<ValidationContainer />} />
            {/* Read users opening Rules or Categories directly are sent back to the dashboard */}
            <Route path="/rules" element={!isDisclaimerViewUser ? <Rules /> : <Navigate to="/mcv" />} />
            <Route path="/report-analysis" element={(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole)) ? <ValidationReportAnalysis /> : <Navigate to="/mcv" />} />
            <Route path="/registry" element={enableLNCFeature ? <Registry /> : <Navigate to="/mcv" />} />
            <Route path="/program-types" element={<ProgramTypes />} />
            <Route path="/categories" element={!isDisclaimerViewUser ? <Categories /> : <Navigate to="/mcv" />} />
            <Route path="/reference-datasets" element={<ReferenceDatasets />} />
            <Route path="/feedbacks" element={<Feedbacks />} />
            <Route path="/rule-test-playground" element={<RuleTestPlayground />} />
            <Route path="/users" element={(userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE) ? <Users /> : <Navigate to="/mcv" />} />
            <Route path="/departments" element={(enableLNCFeature && (userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE)) ? <Departments /> : <Navigate to="/mcv" />} />
            <Route path="/creatives" element={(enableLNCFeature && access.canUseCreatives) ? <Creatives /> : <Navigate to="/mcv" />} />
            <Route path="/send-for-approval/:parentID" element={(enableLNCFeature && ((userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE) && access.canCreate)) ? <SendForApproval /> : <Navigate to="/mcv" />} />
            <Route path="/send-for-approval" element={(enableLNCFeature && ((userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE) && access.canCreate)) ? <SendForApproval /> : <Navigate to="/mcv" />} />
            <Route path="/approval-flows" element={(enableLNCFeature && ((userRole === roles.ADMIN || [roles.SUPERADMIN, roles.OWNER].includes(userRole) || userRole === roles.WRITE) && access.canCreate)) ? <ApprovalFlows /> : <Navigate to="/mcv" />} />
            <Route path="/approval-dashboard" element={(enableLNCFeature && access.canUseApprovals) ? <ApprovalDashboard /> : <Navigate to="/mcv" />} />
            <Route path="/approval-details/:approvalRequestId" element={(enableLNCFeature && access.canUseApprovals) ? <ApprovalDetailedView /> : <Navigate to="/mcv" />} />
            <Route path="/creative-history/:approvalRequestId" element={(enableLNCFeature && access.canUseCreatives) ? <ApprovalDetailedView mode="creator" /> : <Navigate to="/mcv" />} />
            <Route path="/rawdump" element={(enableLNCFeature && access.canUseApprovals) ? <Rawdump /> : <Navigate to="/mcv" />} />
            <Route path="/pending-approvals" element={([roles.SUPERADMIN, roles.OWNER].includes(userRole)) ? <PendingUserApprovals /> : <Navigate to="/mcv" />} />
            {applicationType === 'SaaS' && (
              <>
                <Route path="/usage-dashboard" element={ [roles.SUPERADMIN, roles.OWNER].includes(userRole) ? <UsageDashboard /> : <Navigate to="/mcv" />} />
                <Route path="/subscriptions" element={ [roles.SUPERADMIN, roles.OWNER].includes(userRole) ? <Subscriptions /> : <Navigate to="/mcv" />} />
              </>
            )}
            {/* Catch-all: Redirect any unknown/invalid routes back to Dashboard */}
            <Route path="*" element={<Navigate to="/mcv" replace />} />
          </Routes>
        </Suspense>
      </div>     

    </div>
  );
}

export default HomeScreen;











